import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import URI from 'urijs'
import { Button } from '../../ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../ui/card'
import { CreditCard, Check, ExternalLink, Shield, AlertCircle } from 'lucide-react'

function StripeConnect({ facilityId, facilityName, isConnected, onConnected, onSkip }) {
  const [connectionError, setConnectionError] = useState(false)

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search)
    if (urlParams.get('stripe_connected') === 'true') {
      onConnected()
      window.history.replaceState({}, document.title, window.location.pathname)
    } else if (urlParams.get('error') === 'connection_failed') {
      setConnectionError(true)
      window.history.replaceState({}, document.title, window.location.pathname)
    }
  }, [onConnected])

  const stripeButtonURI = URI({
    protocol: 'https',
    hostname: 'dashboard.stripe.com',
    path: '/oauth/authorize',
    query: {
      response_type: 'code',
      client_id: process.env.STRIPE_CONNECT_CLIENT_ID,
      scope: 'read_write',
      state: `${facilityId}:onboarding`,
      redirect_uri: `${window.location.origin}/api/stripe/new`
    }
  })

  if (isConnected) {
    return (
      <div className="flex items-center justify-center min-h-[80vh] px-4 py-8">
        <Card className="w-full max-w-xl">
          <CardHeader className="space-y-1 text-center">
            <div className="mx-auto w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mb-4">
              <Check className="w-6 h-6 text-green-600" />
            </div>
            <CardTitle className="text-2xl font-bold">Stripe Connected!</CardTitle>
            <CardDescription>{facilityName} is now ready to accept payments</CardDescription>
          </CardHeader>
          <CardContent>
            <Button onClick={onConnected} className="w-full bg-purple-600 hover:bg-purple-700">
              Continue to Next Step
            </Button>
          </CardContent>
        </Card>
      </div>
    )
  }

  return (
    <div className="flex items-center justify-center min-h-[80vh] px-4 py-8">
      <Card className="w-full max-w-xl">
        <CardHeader className="space-y-1 text-center">
          <div className="mx-auto w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center mb-4">
            <CreditCard className="w-6 h-6 text-purple-600" />
          </div>
          <CardTitle className="text-2xl font-bold">Connect Your Stripe Account</CardTitle>
          <CardDescription>Accept payments from parents when they register their children for classes</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {connectionError && (
            <div className="flex items-start gap-3 p-4 bg-red-50 border border-red-200 rounded-lg">
              <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-red-900">Connection failed</p>
                <p className="text-xs text-red-700 mt-1">
                  We couldn&apos;t connect your Stripe account. You can try again below or skip and set up payments later in facility settings.
                </p>
              </div>
            </div>
          )}
          <div className="space-y-4">
            <div className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
              <Shield className="w-5 h-5 text-purple-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-gray-900">Secure & Industry Standard</p>
                <p className="text-xs text-gray-500">
                  Stripe is used by millions of businesses worldwide and handles all payment security for you.
                </p>
              </div>
            </div>

            <div className="space-y-2 text-sm text-gray-600">
              <p className="font-medium text-gray-900">With Stripe, you can:</p>
              <ul className="list-disc list-inside space-y-1 text-gray-600">
                <li>Accept credit/debit cards from parents</li>
                <li>Automatic deposits to your bank account</li>
                <li>Track all payments in your Stripe dashboard</li>
                <li>Issue refunds when needed</li>
              </ul>
            </div>
          </div>

          <div className="space-y-3">
            <a
              href={stripeButtonURI}
              className="flex items-center justify-center w-full py-3 px-4 bg-[#635BFF] hover:bg-[#5851DB] text-white font-medium rounded-md transition-colors"
            >
              <svg className="w-5 h-5 mr-2" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path
                  fillRule="evenodd"
                  clipRule="evenodd"
                  d="M14.4 11.2C14.4 10.16 15.2 9.6 16.64 9.6C18.8 9.6 21.52 10.32 23.68 11.52V5.68C21.36 4.72 19.04 4.32 16.64 4.32C11.36 4.32 7.84 7.12 7.84 11.52C7.84 18.4 17.28 17.28 17.28 20.24C17.28 21.44 16.32 21.92 14.8 21.92C12.4 21.92 9.36 20.88 6.96 19.52V25.44C9.6 26.64 12.24 27.12 14.8 27.12C20.24 27.12 24 24.4 24 19.92C23.92 12.48 14.4 13.84 14.4 11.2Z"
                  fill="white"
                />
              </svg>
              Connect with Stripe
              <ExternalLink className="w-4 h-4 ml-2" />
            </a>

            <Button variant="ghost" onClick={onSkip} className="w-full text-gray-600 hover:text-gray-900">
              Skip for now - I&apos;ll set this up later
            </Button>
          </div>

          <p className="text-xs text-center text-gray-500">
            You can always connect Stripe later in your facility settings. However, you won&apos;t be able to accept
            payments until you do.
          </p>
        </CardContent>
      </Card>
    </div>
  )
}

StripeConnect.propTypes = {
  facilityId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  facilityName: PropTypes.string,
  isConnected: PropTypes.bool,
  onConnected: PropTypes.func.isRequired,
  onSkip: PropTypes.func.isRequired
}

export default StripeConnect
